import React from 'react';
import { Icons } from './studioUi';

interface FileTreeProps {
    files: string[]; // flat relative paths, e.g. "src/App.tsx"
    activeFile: string;
    dirtyPaths: Set<string>; // files with edits not yet flushed to the VFS
    onSelect: (path: string) => void;
    onRename: (path: string) => void;
    onDelete: (path: string) => void;
}

interface TreeNode {
    name: string;
    path: string;
    children?: TreeNode[]; // present => directory
}

/** Build a nested tree from flat paths; dirs sorted first, then files, alpha. */
function buildTree(paths: string[]): TreeNode[] {
    const root: TreeNode[] = [];

    for (const p of paths) {
        const parts = p.split('/');
        let level = root;
        let acc = '';
        parts.forEach((part, i) => {
            acc = acc ? `${acc}/${part}` : part;
            const isFile = i === parts.length - 1;
            let node = level.find((n) => n.name === part);
            if (!node) {
                node = isFile ? { name: part, path: acc } : { name: part, path: acc, children: [] };
                level.push(node);
            }
            if (!isFile) level = node.children!;
        });
    }

    const sortNodes = (nodes: TreeNode[]) => {
        nodes.sort((a, b) => {
            const aDir = !!a.children;
            const bDir = !!b.children;
            if (aDir !== bDir) return aDir ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        nodes.forEach((n) => n.children && sortNodes(n.children));
    };
    sortNodes(root);
    return root;
}

const rowBase: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', gap: 6, minHeight: 28, paddingRight: 8, border: 0, background: 'transparent', fontSize: 12.5, textAlign: 'left', cursor: 'pointer', borderRadius: 8 };
const dot: React.CSSProperties = { flex: 'none', width: 6, height: 6, borderRadius: 999, background: 'var(--hv-clay)' };
const actionBtn: React.CSSProperties = { width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer', borderRadius: 6 };

const NodeRow: React.FC<{
    node: TreeNode;
    depth: number;
    activeFile: string;
    dirtyPaths: Set<string>;
    onSelect: (p: string) => void;
    onRename: (p: string) => void;
    onDelete: (p: string) => void;
}> = ({ node, depth, activeFile, dirtyPaths, onSelect, onRename, onDelete }) => {
    const [open, setOpen] = React.useState(true);

    if (node.children) {
        // A folder is dirty if any descendant file has unflushed edits.
        const dirPrefix = `${node.path}/`;
        const dirDirty = [...dirtyPaths].some((p) => p.startsWith(dirPrefix));
        return (
            <div>
                <button type="button" onClick={() => setOpen((o) => !o)} className="hv-h-page2" style={{ ...rowBase, paddingLeft: depth * 12 + 6, color: 'var(--hv-ink)', fontWeight: 600 }}>
                    <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-muted)' }}>{open ? Icons.chevronD({}) : Icons.chevronR({})}</span>
                    <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-clay)' }}>{open ? Icons.folderOpen({}) : Icons.folderClosed({})}</span>
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
                    {dirDirty && !open && <span title="Unsaved changes inside" style={dot} />}
                </button>
                {open &&
                    node.children.map((child) => (
                        <NodeRow key={child.path} node={child} depth={depth + 1} activeFile={activeFile} dirtyPaths={dirtyPaths} onSelect={onSelect} onRename={onRename} onDelete={onDelete} />
                    ))}
            </div>
        );
    }

    const isActive = node.path === activeFile;
    const isDirty = dirtyPaths.has(node.path);
    // Not a <button>: it hosts nested action buttons (rename/delete), which can't be
    // nested inside a button. Use a clickable row with keyboard support instead.
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect(node.path)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(node.path)}
            className={`hv-ft-row ${isActive ? '' : 'hv-h-page2'}`}
            style={{ ...rowBase, paddingLeft: depth * 12 + 24, background: isActive ? 'var(--hv-mint)' : 'transparent', color: isActive ? 'var(--hv-mint-ink)' : 'var(--hv-body)', fontWeight: isActive ? 600 : 400 }}
        >
            <span style={{ flex: 'none', display: 'flex', color: isActive ? 'var(--hv-mint-ink)' : 'var(--hv-muted)' }}>{Icons.file({})}</span>
            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: isDirty ? 'italic' : 'normal' }}>{node.name}</span>
            {isDirty && <span title="Unsaved changes" style={dot} />}
            <span className="hv-ft-actions" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                <button type="button" title="Rename" aria-label={`Rename ${node.name}`} onClick={(e) => { e.stopPropagation(); onRename(node.path); }} className="hv-h-brand-ink" style={actionBtn}>{Icons.pencil({ size: 11 })}</button>
                <button type="button" title="Delete" aria-label={`Delete ${node.name}`} onClick={(e) => { e.stopPropagation(); onDelete(node.path); }} className="hv-h-danger-ink" style={actionBtn}>{Icons.trash({ size: 11 })}</button>
            </span>
        </div>
    );
};

export const FileTree: React.FC<FileTreeProps> = ({ files, activeFile, dirtyPaths, onSelect, onRename, onDelete }) => {
    const tree = React.useMemo(() => buildTree(files), [files]);
    return (
        <div style={{ padding: '6px 6px 10px' }}>
            {tree.map((node) => (
                <NodeRow key={node.path} node={node} depth={0} activeFile={activeFile} dirtyPaths={dirtyPaths} onSelect={onSelect} onRename={onRename} onDelete={onDelete} />
            ))}
        </div>
    );
};
